/**
 * Font Style presets (PLAT-9973).
 *
 * Three high-level typographic presets — Title, Subtitle, Text — that
 * collapse the legacy 5-role Font Roles system into something a creator
 * can reach for in one click on the Graphics → Text panel. Each preset
 * carries a font family, weight, and size; selecting a preset applies all
 * three to the active text element at once.
 *
 * Org-level overrides live on `ShowcaseBranding.fontStyles` and are
 * merged in by {@link getFontStylePresets}. When no override exists, the
 * hardcoded defaults below ship as-is.
 */

import { parseFontFamilies, parseFontStyles } from './fonts';

import type { FontStyleKey, ShowcaseBranding } from '@src/types.ts/story';

// Re-export so existing callers (TextSettings, TextFontStylePicker) keep
// importing `FontStyleKey` from here. Authoritative definition now lives
// in `@src/types.ts/story` next to `FontStyleOverride`.
export type { FontStyleKey };

export type FontStylePreset = {
  /** Stable identifier for the preset (also used as React keys). */
  key: FontStyleKey;
  /** Title-Case label shown next to / on the preset button. */
  label: string;
  /** Preview text rendered inside the preset button at `previewFontSize`. */
  sample: string;
  /** CSS font-family string applied to the text element. */
  fontFamily: string;
  /** Numeric font weight (e.g. 400, 600). Converted to a string at call time. */
  fontWeight: number;
  /**
   * Pixel size applied to the text element on the *canvas* when the
   * preset is clicked. Larger than `previewFontSize` because the canvas
   * is a much bigger surface than the narrow picker card.
   */
  fontSize: number;
  /**
   * Pixel size used to render the *preview* text inside the picker card.
   * Decoupled from `fontSize` so the card can stay compact while the
   * canvas gets a properly-sized title / subtitle / body.
   */
  previewFontSize: number;
};

/**
 * Default presets shipped with every story until Brand Kit Font Styles
 * configuration lands. Values mirror a typical Title / Subtitle / Body
 * hierarchy and intentionally use Inter (Arbor's default UI font) so the
 * sample previews render even before any brand fonts have loaded.
 */
export const DEFAULT_FONT_STYLE_PRESETS: readonly FontStylePreset[] = [
  {
    key: 'title',
    label: 'Title',
    sample: 'Insert a title',
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 60,
    previewFontSize: 28,
  },
  {
    key: 'subtitle',
    label: 'Subtitle',
    sample: 'Insert a subtitle',
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 36,
    previewFontSize: 20,
  },
  {
    key: 'text',
    label: 'Text',
    sample: 'Insert a little text',
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 32,
    previewFontSize: 14,
  },
];

/**
 * Whether `text` is one of the preset sample strings ("Insert a title",
 * …) that {@link getFontStylePresets} presets pre-fill new text elements
 * with. Samples are never org-overridden (override merging is limited to
 * family / weight / size), so checking the defaults covers every org.
 */
export const isFontStylePlaceholderText = (
  text: string | null | undefined,
): boolean => DEFAULT_FONT_STYLE_PRESETS.some(preset => preset.sample === text);

/**
 * Returns the active Font Style presets, merging any per-org overrides
 * from `branding.fontStyles` onto the {@link DEFAULT_FONT_STYLE_PRESETS}.
 *
 * Override merging is **field-level**: an override that only sets
 * `fontSize` keeps the default family + weight, and vice versa. The
 * `previewFontSize` is never overridden — it's a UI density concern
 * (picker card layout), not a branding choice.
 *
 * When `branding` is undefined / null / has no `fontStyles`, the
 * function returns the defaults unchanged.
 */
export const getFontStylePresets = (
  branding?: ShowcaseBranding | null,
): readonly FontStylePreset[] => {
  // `branding.fontStyles` is a DatoCMS JSON field, so reads return a parsed
  // array. `parseFontStyles` is kept as a defensive guard (legacy strings /
  // store intermediate states) and normalises to [] for any non-array.
  const overrides = parseFontStyles(branding?.fontStyles);
  if (!overrides.length) return DEFAULT_FONT_STYLE_PRESETS;

  const families = parseFontFamilies(branding?.fontFamilies);

  return DEFAULT_FONT_STYLE_PRESETS.map(preset => {
    const override = overrides.find(o => o.key === preset.key);
    if (!override) return preset;

    // Resolve the family name from the brand kit's uploaded fonts. If
    // the family was deleted (or the override predates the family),
    // fall back to the default preset's family string.
    const resolvedFamilyName = override.familyId
      ? families.find(f => f.id === override.familyId)?.name
      : undefined;

    return {
      ...preset,
      fontFamily: resolvedFamilyName ?? preset.fontFamily,
      fontWeight: override.fontWeight ?? preset.fontWeight,
      fontSize: override.fontSize ?? preset.fontSize,
    };
  });
};
